import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Wallet, Users, Award, Package, UserCog, AlertTriangle, CheckCircle, Calendar, Target, BookOpen, AlertOctagon, ArrowUpRight, ArrowDownRight, Minus, BarChart2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import ExportButton from '../components/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#C41E3A', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899'];

const formatCurrency = (value, currency = 'RM') => {
  return `${currency}${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const KPICard = ({ title, value, subtitle, icon: Icon, color = 'red', alert }) => (
  <div className={`bg-white rounded-xl p-4 sm:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow ${alert ? 'ring-2 ring-red-200' : ''}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-gray-500 text-xs sm:text-sm font-medium">{title}</p>
        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mt-1">{value}</p>
        {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
      </div>
      {Icon && (
        <div className={`p-2 sm:p-3 rounded-xl ${color === 'green' ? 'bg-emerald-50' : color === 'blue' ? 'bg-blue-50' : color === 'amber' ? 'bg-amber-50' : color === 'purple' ? 'bg-purple-50' : 'bg-red-50'}`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${color === 'green' ? 'text-emerald-600' : color === 'blue' ? 'text-blue-600' : color === 'amber' ? 'text-amber-600' : color === 'purple' ? 'text-purple-600' : 'text-red-700'}`} />
        </div>
      )}
    </div>
  </div>
);

const AlertBadge = ({ count, label, color }) => count > 0 && (
  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${color}`}>
    <AlertTriangle className="w-3 h-3" /> {count} {label}
  </div>
);

// Comparison Card Component
const ComparisonCard = ({ title, current, previous, change, trend, isCurrency = true, isGood = true }) => {
  const isPositive = change > 0;
  const trendColor = isGood ? (isPositive ? 'text-emerald-600' : 'text-red-600') : (isPositive ? 'text-red-600' : 'text-emerald-600');
  const bgColor = isGood ? (isPositive ? 'bg-emerald-50' : 'bg-red-50') : (isPositive ? 'bg-red-50' : 'bg-emerald-50');
  
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${trendColor}`}>
          {isPositive ? <ArrowUpRight className="w-3 h-3" /> : change < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          {Math.abs(change)}%
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <span className="text-xs text-gray-400">Bulan Ini</span>
          <span className="text-lg font-bold text-gray-800">
            {isCurrency ? formatCurrency(current) : current}
          </span>
        </div>
        <div className="flex items-end justify-between">
          <span className="text-xs text-gray-400">Bulan Lepas</span>
          <span className="text-sm text-gray-500">
            {isCurrency ? formatCurrency(previous) : previous}
          </span>
        </div>
      </div>
    </div>
  );
};

const OverviewPage = () => {
  const { revenueList, expenseList, cashflowList, staffList, loading } = useApp();
  const [assetData, setAssetData] = useState(null);
  const [hrData, setHrData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [extraLoading, setExtraLoading] = useState(true);

  useEffect(() => {
    const fetchExtra = async () => {
      try {
        const [assetRes, hrRes, compRes, unifiedRes] = await Promise.all([
          axios.get(`${API}/assets/summary/dashboard`),
          axios.get(`${API}/hr/dashboard`),
          axios.get(`${API}/dashboard/comparison`),
          axios.get(`${API}/unified/overview`)
        ]);
        setAssetData(assetRes.data);
        setHrData(hrRes.data);
        setComparisonData(compRes.data);
        // Store unified data for consolidated view
        window.unifiedData = unifiedRes.data;
      } catch (e) { console.error(e); }
      finally { setExtraLoading(false); }
    };
    fetchExtra();
  }, []);

  if (loading || extraLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  // Safe arrays
  const safeRevenueList = Array.isArray(revenueList) ? revenueList : [];
  const safeExpenseList = Array.isArray(expenseList) ? expenseList : [];
  const safeCashflowList = Array.isArray(cashflowList) ? cashflowList : [];
  const safeStaffList = Array.isArray(staffList) ? staffList : [];

  // Financial calculations
  const totalRevenue = safeRevenueList.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalExpenses = safeExpenseList.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netProfit = totalRevenue - totalExpenses;
  const cashBalance = safeCashflowList[0]?.closing_balance || 0;

  // Staff & Payroll
  const totalStaff = safeStaffList.length;
  const activeStaff = safeStaffList.filter(s => s.status === 'Active').length;
  const totalGrossSalary = safeStaffList.reduce((sum, s) => sum + (s.gross_salary || 0), 0);
  const totalNetPay = safeStaffList.reduce((sum, s) => sum + (s.net_pay || 0), 0);

  // Asset data
  const totalAssets = assetData?.total_assets || 0;
  const assetPurchaseValue = assetData?.total_purchase_value || 0;
  const assetCurrentValue = assetData?.total_current_value || 0;
  const assetDepreciation = assetData?.total_depreciation || 0;
  const assetAlerts = assetData?.alerts?.total_alerts || 0;

  // HR data
  const hrSummary = hrData?.summary || {};
  const hrAlerts = (hrSummary.active_warnings || 0) + (hrSummary.staff_low_performance || 0) + (hrSummary.pending_leaves || 0);
  const attendanceToday = hrData?.attendance_today || {};
  const totalHadir = attendanceToday.hadir || 0;

  // Comparison data
  const comparison = comparisonData?.summary || {};
  const hrComparison = comparisonData?.hr || {};
  const breakdown = comparisonData?.breakdown || {};

  // Expense breakdown
  const expenseBreakdown = Object.entries(safeExpenseList.reduce((acc, item) => {
    const cat = item.expense_category || 'Other';
    acc[cat] = (acc[cat] || 0) + (item.amount || 0);
    return acc;
  }, {})).map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount);

  // Revenue by source
  const revenueBySource = Object.entries(safeRevenueList.reduce((acc, item) => {
    const src = item.revenue_source || 'Other';
    acc[src] = (acc[src] || 0) + (item.amount || 0);
    return acc;
  }, {})).map(([source, amount]) => ({ source, amount })).sort((a, b) => b.amount - a.amount);

  const revenueTrend = [...safeRevenueList].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Comparison chart data
  const comparisonChartData = [
    { name: 'Revenue', current: comparison.revenue?.current || 0, previous: comparison.revenue?.previous || 0 },
    { name: 'Expenses', current: comparison.expenses?.current || 0, previous: comparison.expenses?.previous || 0 },
    { name: 'Profit', current: comparison.profit?.current || 0, previous: comparison.profit?.previous || 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Overview</h1>
          <p className="text-gray-500 text-sm">Ringkasan keseluruhan syarikat - Kewangan, HR & Aset</p>
        </div>
        <ExportButton type="all" />
      </div>

      {/* Alerts Banner */}
      {(assetAlerts > 0 || hrAlerts > 0) && (
        <div className="bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded-xl p-3 flex flex-wrap gap-2 items-center">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <span className="text-sm font-medium text-gray-700">Perlu Perhatian:</span>
          <AlertBadge count={hrSummary.active_warnings} label="amaran aktif" color="bg-red-100 text-red-700" />
          <AlertBadge count={hrSummary.staff_low_performance} label="prestasi rendah" color="bg-orange-100 text-orange-700" />
          <AlertBadge count={hrSummary.pending_leaves} label="cuti pending" color="bg-blue-100 text-blue-700" />
          <AlertBadge count={assetData?.alerts?.maintenance_overdue?.length} label="maintenance tertunggak" color="bg-purple-100 text-purple-700" />
        </div>
      )}

      {/* Month Comparison Section - NEW */}
      <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-xl p-4 sm:p-6 border border-red-100">
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 className="w-5 h-5 text-red-700" />
          <h2 className="text-lg font-semibold text-gray-800">Perbandingan Bulan</h2>
          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
            {comparisonData?.periods?.current || ''} vs {comparisonData?.periods?.previous || ''}
          </span>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ComparisonCard 
            title="Revenue" 
            current={comparison.revenue?.current} 
            previous={comparison.revenue?.previous}
            change={comparison.revenue?.change || 0}
            trend={comparison.revenue?.trend}
            isGood={true}
          />
          <ComparisonCard 
            title="Expenses" 
            current={comparison.expenses?.current} 
            previous={comparison.expenses?.previous}
            change={comparison.expenses?.change || 0}
            trend={comparison.expenses?.trend}
            isGood={false}
          />
          <ComparisonCard 
            title="Net Profit" 
            current={comparison.profit?.current} 
            previous={comparison.profit?.previous}
            change={comparison.profit?.change || 0}
            trend={comparison.profit?.trend}
            isGood={true}
          />
        </div>

        {/* Comparison Bar Chart */}
        <div className="mt-6 bg-white rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Visual Perbandingan</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={comparisonChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => `RM${(v/1000).toFixed(0)}k`} />
              <YAxis dataKey="name" type="category" tick={{ fill: '#6b7280', fontSize: 11 }} width={60} />
              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
              <Legend />
              <Bar dataKey="current" fill="#C41E3A" name="Bulan Ini" radius={[0, 4, 4, 0]} />
              <Bar dataKey="previous" fill="#9CA3AF" name="Bulan Lepas" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* HR Comparison */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 mb-1">Kehadiran</p>
            <p className="text-lg font-bold text-gray-800">{hrComparison.attendance?.current || 0}</p>
            <p className="text-xs text-gray-400">vs {hrComparison.attendance?.previous || 0}</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 mb-1">Lewat</p>
            <p className="text-lg font-bold text-red-600">{hrComparison.late?.current || 0}</p>
            <p className="text-xs text-gray-400">vs {hrComparison.late?.previous || 0}</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 mb-1">OT (jam)</p>
            <p className="text-lg font-bold text-amber-600">{hrComparison.overtime?.current || 0}</p>
            <p className="text-xs text-gray-400">vs {hrComparison.overtime?.previous || 0}</p>
          </div>
        </div>
      </div>

      {/* Financial KPIs */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> KEWANGAN</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPICard title="Total Revenue" value={formatCurrency(totalRevenue)} subtitle={`${safeRevenueList.length} entries`} icon={TrendingUp} color="green" />
          <KPICard title="Total Expenses" value={formatCurrency(totalExpenses)} subtitle={`${safeExpenseList.length} entries`} icon={TrendingDown} color="red" />
          <KPICard title="Net Profit" value={formatCurrency(netProfit)} subtitle={netProfit >= 0 ? 'Profitable' : 'Loss'} icon={DollarSign} color={netProfit >= 0 ? 'green' : 'red'} />
          <KPICard title="Cash Balance" value={formatCurrency(cashBalance)} subtitle="Latest closing" icon={Wallet} color="blue" />
        </div>
      </div>

      {/* HR & Staff KPIs */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> SUMBER MANUSIA</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <KPICard title="Staf Aktif" value={activeStaff.toString()} subtitle={`${totalStaff} total`} icon={Users} color="blue" />
          <KPICard title="Hadir Hari Ini" value={totalHadir.toString()} subtitle={`${attendanceToday.cuti || 0} cuti, ${attendanceToday.mc || 0} MC`} icon={Calendar} color="green" />
          <KPICard title="Gaji Bulanan" value={`RM${totalGrossSalary.toLocaleString()}`} subtitle={`Net: RM${totalNetPay.toLocaleString()}`} icon={Wallet} color="amber" />
          <KPICard title="Perlu Latihan" value={(hrSummary.staff_need_training || 0).toString()} subtitle="Flagged for training" icon={BookOpen} color={hrSummary.staff_need_training > 0 ? 'amber' : 'green'} alert={hrSummary.staff_need_training > 3} />
          <KPICard title="Amaran Aktif" value={(hrSummary.active_warnings || 0).toString()} subtitle="Disciplinary" icon={AlertOctagon} color={hrSummary.active_warnings > 0 ? 'red' : 'green'} alert={hrSummary.active_warnings > 0} />
        </div>
      </div>

      {/* Asset KPIs */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> ASET</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPICard title="Jumlah Aset" value={totalAssets.toString()} subtitle={`${assetData?.by_category?.length || 0} kategori`} icon={Package} color="blue" />
          <KPICard title="Nilai Belian" value={`RM${assetPurchaseValue.toLocaleString()}`} subtitle="Original cost" icon={DollarSign} color="green" />
          <KPICard title="Nilai Semasa" value={`RM${assetCurrentValue.toLocaleString()}`} subtitle={`Susut: RM${assetDepreciation.toLocaleString()}`} icon={TrendingDown} color="amber" />
          <KPICard title="Asset Alerts" value={assetAlerts.toString()} subtitle="Maintenance & warranty" icon={AlertTriangle} color={assetAlerts > 0 ? 'red' : 'green'} alert={assetAlerts > 0} />
        </div>
      </div>

      {/* PM Sales removed */}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Revenue Trend</h3>
          {revenueTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                <Area type="monotone" dataKey="amount" stroke="#10B981" fill="#10B981" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-[250px] flex items-center justify-center text-gray-400">No revenue data yet</div>}
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Expense Breakdown</h3>
          {expenseBreakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={expenseBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="amount" nameKey="category">
                    {expenseBreakdown.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-2 mt-2">
                {expenseBreakdown.slice(0, 5).map((item, index) => (
                  <div key={item.category} className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <span className="text-gray-600">{item.category}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="h-[250px] flex items-center justify-center text-gray-400">No expense data yet</div>}
        </div>
      </div>

      {/* Revenue by Source Comparison - NEW */}
      {breakdown.revenue_by_source?.length > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Perbandingan Revenue by Source</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Sumber</th>
                  <th className="text-right p-3 font-medium text-gray-600">Bulan Ini</th>
                  <th className="text-right p-3 font-medium text-gray-600">Bulan Lepas</th>
                  <th className="text-right p-3 font-medium text-gray-600">Perubahan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {breakdown.revenue_by_source.map(item => (
                  <tr key={item.source} className="hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-700">{item.source}</td>
                    <td className="p-3 text-right text-emerald-600 font-medium">{formatCurrency(item.current)}</td>
                    <td className="p-3 text-right text-gray-500">{formatCurrency(item.previous)}</td>
                    <td className="p-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        item.change > 0 ? 'bg-emerald-100 text-emerald-700' : item.change < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {item.change > 0 ? <ArrowUpRight className="w-3 h-3" /> : item.change < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                        {Math.abs(item.change)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Table */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-medium mb-4">Ringkasan Keseluruhan</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {/* Financial */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">Kewangan</h4>
            <div className="flex justify-between p-2 bg-emerald-50 rounded"><span className="text-gray-700">Revenue</span><span className="font-bold text-emerald-600">{formatCurrency(totalRevenue)}</span></div>
            <div className="flex justify-between p-2 bg-red-50 rounded"><span className="text-gray-700">Expenses</span><span className="font-bold text-red-600">{formatCurrency(totalExpenses)}</span></div>
            <div className="flex justify-between p-2 bg-amber-50 rounded"><span className="text-gray-700">Payroll</span><span className="font-bold text-amber-600">${totalGrossSalary.toLocaleString()}</span></div>
            <div className={`flex justify-between p-2 rounded ${netProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <span className="font-medium">Net Profit</span>
              <span className={`font-bold ${netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(netProfit)}</span>
            </div>
          </div>
          
          {/* HR */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">Sumber Manusia</h4>
            <div className="flex justify-between p-2 bg-blue-50 rounded"><span className="text-gray-700">Staf Aktif</span><span className="font-bold text-blue-600">{activeStaff}</span></div>
            <div className="flex justify-between p-2 bg-emerald-50 rounded"><span className="text-gray-700">Telah Dinilai</span><span className="font-bold text-emerald-600">{hrSummary.total_evaluated || 0}</span></div>
            <div className="flex justify-between p-2 bg-amber-50 rounded"><span className="text-gray-700">Perlu Latihan</span><span className="font-bold text-amber-600">{hrSummary.staff_need_training || 0}</span></div>
            <div className="flex justify-between p-2 bg-purple-50 rounded"><span className="text-gray-700">Turnover YTD</span><span className="font-bold text-purple-600">{hrSummary.turnover_ytd || 0}</span></div>
          </div>
          
          {/* Assets */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">Aset</h4>
            <div className="flex justify-between p-2 bg-blue-50 rounded"><span className="text-gray-700">Jumlah Aset</span><span className="font-bold text-blue-600">{totalAssets}</span></div>
            <div className="flex justify-between p-2 bg-emerald-50 rounded"><span className="text-gray-700">Nilai Semasa</span><span className="font-bold text-emerald-600">${assetCurrentValue.toLocaleString()}</span></div>
            <div className="flex justify-between p-2 bg-red-50 rounded"><span className="text-gray-700">Susut Nilai</span><span className="font-bold text-red-600">${assetDepreciation.toLocaleString()}</span></div>
            <div className="flex justify-between p-2 bg-amber-50 rounded"><span className="text-gray-700">Alerts</span><span className="font-bold text-amber-600">{assetAlerts}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
